import Img from '@Components/common/Img';
import MetaTags from '@Components/common/OpenGraphTags/MetaTags';
import TameeniProductsList from '@Components/Home/TameeniProducts';
import { useHomeLinks } from '@Components/Layout/hooks/useHomeLinks';
import { IFAQsQuestion } from '@models/faq.model';
import CustomMainNav from '@Pages/MainNav';
import {
  FaqSection,
  Hero,
  HeroSection,
  Home,
  MobileAppBanner,
  PartnerItem,
  Partners,
  SingleShipmentSection,
  WhyTameeniCard,
  WhyTameeniSection,
} from '@tameeni/home';
import { IconName } from '@tameeni/icons';
import { Products } from '@tameeni/shared-logic';
import { FaqAccordion, FaqItem } from '@tameeni/ui';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const IC_LOGOS = ['walaa'];

export default function PSHomePage() {
  const {
    t,
    i18n: { language },
  } = useTranslation(['psHome', 'routes', 'home', 'meta']);
  const { STEP_ONE_LINKS } = useHomeLinks();
  const locale = language as 'ar' | 'en';

  useEffect(() => {
    localStorage.removeItem('ph');
    localStorage.removeItem('wasAuth');
  }, []);

  const whyTameeniCards = Array.from({ length: 8 }, (_, i) => ({
    description: t(`psHome:why_tameeni.cards.st${i + 1}.description`),
    icon: [
      'PsBenefitProgram',
      'PsBenefitProtection',
      'PsBenefitStrategy',
      'PsBenefitCompare',
      'PsBenefitIssue',
      'PsBenefitPayment',
      'PsBenefitMonitor',
      'PsBenefitAlert',
    ][i],
    title: t(`psHome:why_tameeni.cards.st${i + 1}.title`),
  }));

  const mulipleReasons = Array.from({ length: 7 }, (_, i) => ({
    description: t(`psHome:muliple_reasons.cards.st${i + 1}.description`),
    title: t(`psHome:muliple_reasons.cards.st${i + 1}.title`),
  }));

  const handleGetQuote = () => {
    window.location.href = STEP_ONE_LINKS.protection_and_saving;
  };

  return (
    <>
      <MetaTags
        title={t('meta:ps.meta.title')}
        description={t('meta:ps.meta.description')}
        origin={window.settings.PS_ORIGIN}
        kw={t('meta:ps.meta.kw')}
      />
      <Home currentActiveTab={Products.PROTECTION_AND_SAVING} locale={locale}>
        <CustomMainNav />

        <HeroSection className='z-[1]'>
          <Hero.HeroContent title={t('psHome:hero.title')} subTitle={t('psHome:hero.description')}>
            <div id='get-quote-action'>
              <Hero.Actions>
                <Hero.ActionButton
                  label={t('psHome:hero.get_quote')}
                  actionType='primary'
                  onClick={handleGetQuote}
                />
              </Hero.Actions>
            </div>
          </Hero.HeroContent>
          <Hero.MediaSection
            type='video'
            videoData={{
              src: '/Resources/images/home/hero-banner/life-video.mp4',
              text: 'Tameeni Protection and Saving',
              poster: '/Resources/images/home/hero-banner/life-desktop.webp',
            }}
            alt='Tameeni Protection and Saving'
            locale={locale}
            // videoClassName='lg:h-full lg:absolute lg:top-0 lg:end-0 lg:w-auto rtl:scale-x-[-1]'
          />
        </HeroSection>

        <Partners includedSponsors={['IA']} isCentered>
          {IC_LOGOS?.map((logo) => {
            return (
              <PartnerItem key={logo}>
                <Img
                  className='h-[30px] hover:opacity-70'
                  src={`/Resources/images/ICLogos/${logo}.svg`}
                  title={logo}
                />
              </PartnerItem>
            );
          })}
        </Partners>

        <div className='py-8 md:py-16'>
          <TameeniProductsList />
        </div>

        <SingleShipmentSection
          contentTitle={t('psHome:why_protection.title')}
          sectionSubtitle=''
          sectionTitle=''
        >
          <React.Fragment key='.0'>
            <span>{t('psHome:why_protection.description')}</span>
          </React.Fragment>
        </SingleShipmentSection>
        <div className='w-full overflow-hidden'></div>
        <WhyTameeniSection title={t('psHome:why_tameeni.title')}>
          {whyTameeniCards.map((item) => (
            <WhyTameeniCard
              key={item.icon}
              icon={item.icon as IconName}
              title={''}
              description={item.description}
            />
          ))}
        </WhyTameeniSection>

        <div className='bg-slate-800 py-8 lg:py-16'>
          <WhyTameeniSection className='text-white my-0' title={t('psHome:muliple_reasons.title')}>
            {mulipleReasons.map((item) => (
              <WhyTameeniCard
                key={item.title}
                title={item.title}
                description={item.description}
                className='bg-slate-700 text-white'
                descriptionClassName='text-slate-200 whitespace-pre-line'
              />
            ))}
          </WhyTameeniSection>
        </div>

        <FaqSection
          title={t('psHome:tips.title')}
          link={`${window.settings.PS_ORIGIN}/${locale}/faqs`}
          linkLabel={t('psHome:tips.viewMore')}
        >
          <FaqAccordion>
            {(t('psHome:tips.questions', { returnObjects: true }) as IFAQsQuestion[])?.map(
              (question) => {
                return (
                  <FaqItem key={question?.q} title={question?.q}>
                    {question?.a}
                  </FaqItem>
                );
              },
            )}
          </FaqAccordion>
        </FaqSection>
        {/* Mobile App Banner */}
        <div className='mt-32 overflow-x-clip'>
          <MobileAppBanner
            locale={locale}
            title={t('home:home_app.title')}
            text={t('home:home_app.subtitle')}
          />
        </div>
      </Home>
    </>
  );
}
